/**
 * Deckface global cart synchronizer.
 *
 * Solves custom Add to Cart implementations that bypass
 * Horizon's StandardEvents.cartLinesUpdate pipeline.
 *
 * Works for:
 * - product page
 * - homepage custom sections
 * - collection cards
 * - quick add
 * - recommendations
 * - custom AJAX add-to-cart buttons
 *
 * Behaviour:
 * - cart/add -> open drawer immediately
 * - successful cart mutation -> fetch fresh cart-drawer-section
 * - update drawer DOM
 * - update Deckface header badge
 *
 * It does NOT intercept GET /cart.js.
 */

const CART_DRAWER_SECTION_ID = 'cart-drawer-section';

const CART_MUTATION_PATTERN =
  /\/cart\/(?:add|change|update)(?:\.js)?(?:\?|$)/i;

const CART_ADD_PATTERN =
  /\/cart\/add(?:\.js)?(?:\?|$)/i;


/* ============================================================
   STATE
============================================================ */

let refreshingDrawer = false;
let refreshQueued = false;

const originalFetch =
  window.fetch.bind(window);


/* ============================================================
   HELPERS
============================================================ */

function normalizeUrl(input) {
  try {
    if (input instanceof Request) {
      return new URL(input.url, window.location.origin);
    }

    return new URL(
      String(input),
      window.location.origin
    );
  } catch {
    return null;
  }
}


function isCartMutation(input) {
  const url =
    normalizeUrl(input);

  if (!url) {
    return false;
  }

  return CART_MUTATION_PATTERN.test(
    url.pathname
  );
}


function isCartAdd(input) {
  const url =
    normalizeUrl(input);

  if (!url) {
    return false;
  }

  return CART_ADD_PATTERN.test(
    url.pathname
  );
}


/* ============================================================
   FIND DRAWER

   There must only be ONE #cart-drawer.
============================================================ */

function getCartDrawer() {
  return document.getElementById(
    'cart-drawer'
  );
}


/* ============================================================
   OPEN DRAWER
============================================================ */

function openCartDrawer() {
  const drawer =
    getCartDrawer();

  if (
    !drawer ||
    typeof drawer.open !== 'function'
  ) {
    return;
  }

  if (!drawer.isOpen) {
    drawer.open();
  }
}


/* ============================================================
   HEADER COUNT
============================================================ */

function paintHeaderCount(count) {
  const number =
    Number(count) || 0;

  document
    .querySelectorAll(
      '[data-dh-count]'
    )
    .forEach((badge) => {
      badge.textContent =
        String(number);

      if (number > 0) {
        badge.removeAttribute(
          'hidden'
        );
      } else {
        badge.setAttribute(
          'hidden',
          ''
        );
      }
    });


  document
    .querySelectorAll(
      '[data-dh-cart]'
    )
    .forEach((cartButton) => {
      if (number > 0) {
        cartButton.removeAttribute(
          'hidden'
        );
      } else {
        cartButton.setAttribute(
          'hidden',
          ''
        );
      }
    });
}


/* ============================================================
   FETCH AUTHORITATIVE CART COUNT
============================================================ */

async function refreshHeaderCount() {
  try {
    const root =
      window.Shopify?.routes?.root ||
      '/';

    const response =
      await originalFetch(
        `${root}cart.js`,
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json'
          },

          cache:
            'no-store'
        }
      );


    if (!response.ok) {
      return;
    }


    const cart =
      await response.json();


    paintHeaderCount(
      cart.item_count
    );


    document.dispatchEvent(
      new CustomEvent(
        'deckface:cart-count',
        {
          detail: {
            cart,
            itemCount:
              cart.item_count
          }
        }
      )
    );

  } catch (error) {
    console.warn(
      '[deckface-cart] Unable to refresh header count:',
      error
    );
  }
}


/* ============================================================
   FETCH CART DRAWER SECTION

   Uses Shopify's Section Rendering API.

   This is deliberately a GET request, therefore our fetch
   interceptor below will NOT identify it as a cart mutation.
============================================================ */

async function fetchDrawerSection() {
  const url =
    new URL(
      window.location.href
    );


  /*
    IMPORTANT:
    use sections, plural.

    Shopify returns:
    {
      "cart-drawer-section": "..."
    }
  */

  url.searchParams.set(
    'sections',
    CART_DRAWER_SECTION_ID
  );


  /*
    Prevent browser/CDN cache from supplying stale cart HTML.
  */

  url.searchParams.set(
    '_deckface_cart',
    Date.now().toString()
  );


  const response =
    await originalFetch(
      url.toString(),
      {
        method: 'GET',

        headers: {
          Accept:
            'application/json'
        },

        cache:
          'no-store'
      }
    );


  if (!response.ok) {
    throw new Error(
      `Drawer section request failed: ${response.status}`
    );
  }


  const sections =
    await response.json();


  return sections?.[
    CART_DRAWER_SECTION_ID
  ] || '';
}


/* ============================================================
   REPLACE DRAWER INNER

   Keep:
   - current <theme-drawer>
   - current <dialog>
   - current modal/top-layer state

   Replace only:
   [data-hydration-key="cart-drawer-inner"]
============================================================ */

function replaceDrawerInner(html) {
  if (!html) {
    console.warn(
      '[deckface-cart] Empty drawer section HTML.'
    );

    return false;
  }


  const parser =
    new DOMParser();


  const doc =
    parser.parseFromString(
      html,
      'text/html'
    );


  const incomingDrawer =
    doc.querySelector(
      '#cart-drawer'
    );


  if (!incomingDrawer) {
    console.warn(
      '[deckface-cart] Fresh #cart-drawer missing.'
    );

    return false;
  }


  const incomingInner =
    incomingDrawer.querySelector(
      '[data-hydration-key="cart-drawer-inner"]'
    );


  const currentDrawer =
    getCartDrawer();


  const currentInner =
    currentDrawer?.querySelector(
      '[data-hydration-key="cart-drawer-inner"]'
    );


  if (
    !incomingInner ||
    !currentInner
  ) {
    console.warn(
      '[deckface-cart] cart-drawer-inner missing.'
    );

    return false;
  }


  /*
    Clone into the live document.
  */

  const replacement =
    document.importNode(
      incomingInner,
      true
    );


  currentInner.replaceWith(
    replacement
  );


  document.dispatchEvent(
    new CustomEvent(
      'deckface:cart-rendered'
    )
  );


  return true;
}


/* ============================================================
   REFRESH DRAWER
============================================================ */

export async function refreshDeckfaceCartDrawer() {
  if (refreshingDrawer) {
    refreshQueued =
      true;

    return;
  }


  refreshingDrawer =
    true;


  try {
    /*
      Header and drawer can update in parallel.
    */

    const [
      html
    ] =
      await Promise.all([
        fetchDrawerSection(),
        refreshHeaderCount()
      ]);


    replaceDrawerInner(
      html
    );

  } catch (error) {
    console.warn(
      '[deckface-cart] Drawer refresh failed:',
      error
    );

    /*
      Header should still be authoritative even if
      section rendering temporarily failed.
    */

    refreshHeaderCount();

  } finally {
    refreshingDrawer =
      false;


    if (refreshQueued) {
      refreshQueued =
        false;

      refreshDeckfaceCartDrawer();
    }
  }
}


/* ============================================================
   GLOBAL FETCH BRIDGE

   This is the important part for homepage/custom buttons.

   Any code anywhere on the storefront doing:

   fetch('/cart/add.js', ...)
   fetch('/cart/change.js', ...)
   fetch('/cart/update.js', ...)

   automatically participates in drawer synchronization.
============================================================ */

window.fetch =
  async function deckfaceFetch(
    input,
    init
  ) {
    const mutation =
      isCartMutation(input);


    const add =
      isCartAdd(input);


    /*
      ADD:
      show the drawer immediately rather than waiting
      for the network request to finish.
    */

    if (add) {
      openCartDrawer();
    }


    const response =
      await originalFetch(
        input,
        init
      );


    if (
      mutation &&
      response.ok
    ) {
      /*
        Do not hold up the original caller.

        Clone isn't needed because we don't consume
        the caller's response at all.
      */

      queueMicrotask(
        () => {
          refreshDeckfaceCartDrawer();
        }
      );
    }


    return response;
  };


/* ============================================================
   HORIZON EVENT BRIDGE

   Native Horizon product pages still use cartLinesUpdate.

   Listening here means product-page and custom-page behaviour
   converge on the same refresh implementation.
============================================================ */

import('@shopify/events')
  .then(
    ({ StandardEvents }) => {
      if (
        !StandardEvents?.cartLinesUpdate
      ) {
        return;
      }


      document.addEventListener(
        StandardEvents.cartLinesUpdate,
        (event) => {
          /*
            Native Add:
            open immediately.
          */

          if (
            event.action === 'add'
          ) {
            openCartDrawer();
          }


          /*
            If Horizon supplies an async completion Promise,
            perform one authoritative refresh when complete.

            The fetch bridge may already have queued one;
            refreshQueued prevents conflicting requests.
          */

          if (
            event.promise &&
            typeof event.promise.then ===
            'function'
          ) {
            event.promise
              .then(
                ({ detail } = {}) => {
                  if (
                    !detail?.didError
                  ) {
                    refreshDeckfaceCartDrawer();
                  }
                }
              )
              .catch(
                () => {}
              );
          }
        }
      );
    }
  )
  .catch(
    () => {}
  );


/* ============================================================
   CUSTOM EVENTS
============================================================ */

[
  'cart:refresh',
  'cart:update',
  'cart-updated',
  'variant:add'
]
.forEach(
  (eventName) => {
    document.addEventListener(
      eventName,
      () => {
        refreshDeckfaceCartDrawer();
      }
    );
  }
);


/* ============================================================
   PUBLIC API FOR DEBUG / CUSTOM CODE
============================================================ */

window.DeckfaceCart = {
  refresh:
    refreshDeckfaceCartDrawer,

  open:
    openCartDrawer,

  refreshCount:
    refreshHeaderCount
};


/* ============================================================
   INITIAL HEADER COUNT
============================================================ */

refreshHeaderCount();